import { useEffect, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * Three-screen welcome for the public KFZ form. Mirrors the
 * Risikoanalyse welcome engine (auto-advance + skippable + dot indicator)
 * but with KFZ-themed copy and a 4-tile data-cluster graphic recreated in
 * inline SVG (Fahrzeugart / Kennzeichen / Marke·Modell / Wert).
 */

const useStyles = makeStyles({
  root: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.ink,
    backgroundImage: `linear-gradient(135deg, ${BRAND.ink} 0%, ${BRAND.purple} 60%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  topBar: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "16px",
  },
  skipBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "rgba(255,255,255,0.06)",
    backgroundImage: "none",
    color: "rgba(255,255,255,0.85)",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.2)"),
    ...shorthands.borderRadius("4px"),
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.12)",
    },
  },
  stage: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: "80px",
  },
  pageOneWrap: { textAlign: "center" },
  eyebrow: {
    marginBottom: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    letterSpacing: "0.4em",
    textTransform: "uppercase",
    color: "rgba(247,168,35,0.85)",
    animationDuration: "600ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
  },
  hero: {
    fontFamily: BRAND.fontAccent,
    color: BRAND.gold,
    fontSize: "clamp(2.5rem, 9vw, 6rem)",
    lineHeight: 1.05,
    fontWeight: 400,
    margin: 0,
    animationDuration: "700ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  heroSub: {
    marginTop: "8px",
    fontFamily: BRAND.fontAccent,
    color: "rgba(255,255,255,0.95)",
    fontSize: "clamp(1.6rem, 5vw, 2.4rem)",
    animationDuration: "1100ms",
    animationDelay: "300ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  pageTwo: {
    maxWidth: "740px",
    textAlign: "center",
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 2rem)",
    lineHeight: 1.45,
    color: "rgba(255,255,255,0.95)",
    animationDuration: "700ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  bold: { color: BRAND.gold, fontWeight: 700 },
  tilesWrap: {
    width: "100%",
    maxWidth: "1024px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, #3a1c52 100%)`,
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 24px 48px rgba(0,0,0,0.3)",
  },
  tilesHeading: {
    marginTop: 0,
    marginBottom: "20px",
    color: BRAND.gold,
    fontFamily: BRAND.fontAccent,
    fontWeight: 400,
    fontSize: "clamp(1.1rem, 3.6vw, 1.6rem)",
    lineHeight: 1.2,
  },
  tileRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "16px",
    },
  },
  tile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    color: BRAND.white,
    boxShadow: "0 6px 12px rgba(0,0,0,0.25)",
    minHeight: "180px",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "14px",
    paddingRight: "14px",
    textAlign: "center",
    backgroundColor: BRAND.pillarOrange,
    ...shorthands.borderRadius("8px"),
  },
  tileNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    fontWeight: 700,
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    fontSize: "0.85rem",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    ...shorthands.borderRadius("999px"),
  },
  tileLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 600,
    lineHeight: 1.25,
  },
  pageThreeNote: {
    maxWidth: "640px",
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: "20px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    textAlign: "center",
    color: "rgba(255,255,255,0.7)",
  },
  footer: {
    position: "absolute",
    insetInlineStart: 0,
    insetInlineEnd: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    paddingBottom: "24px",
  },
  footerInner: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  dots: { display: "flex", alignItems: "center", columnGap: "6px" },
  dot: {
    height: "6px",
    width: "24px",
    backgroundColor: "rgba(255,255,255,0.25)",
    transitionProperty: "background-color",
    transitionDuration: "200ms",
    ...shorthands.borderRadius("999px"),
  },
  dotActive: { backgroundColor: BRAND.gold },
  weiterBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.gold,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 700,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.gold),
    ...shorthands.borderRadius("6px"),
    ":hover": {
      backgroundColor: "#e89d20",
      ...shorthands.borderColor("#e89d20"),
    },
  },
});

const PAGE_DURATIONS_MS = [3000, 6000, 6000];

const TileIcon = {
  car: () => (
    <svg width="40" height="28" viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 24l3-9a3 3 0 0 1 2.8-2h22.4a3 3 0 0 1 2.8 2l3 9" />
      <path d="M4 24h44v6H4z" />
      <circle cx="14" cy="28" r="2.5" />
      <circle cx="38" cy="28" r="2.5" />
    </svg>
  ),
  plate: () => (
    <svg width="40" height="28" viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="52" height="24" rx="3" />
      <rect x="2" y="6" width="10" height="24" />
      <line x1="20" y1="18" x2="48" y2="18" />
    </svg>
  ),
  brand: () => (
    <svg width="40" height="28" viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="6" width="22" height="10" rx="2" />
      <rect x="30" y="6" width="22" height="10" rx="2" />
      <rect x="4" y="20" width="22" height="10" rx="2" />
      <rect x="30" y="20" width="22" height="10" rx="2" />
    </svg>
  ),
  euro: () => (
    <svg width="40" height="28" viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="28" cy="18" r="14" />
      <path d="M36 12a10 10 0 1 0 0 12" />
      <path d="M18 16h12M18 22h12" />
    </svg>
  ),
};

type Props = {
  companyName: string;
  onComplete: () => void;
};

const KfzWelcomeFlow = ({ companyName, onComplete }: Props) => {
  const styles = useStyles();
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (page > 2) return;
    const t = window.setTimeout(() => {
      if (page === 2) onComplete();
      else setPage((p) => p + 1);
    }, PAGE_DURATIONS_MS[page]);
    return () => window.clearTimeout(t);
  }, [page, onComplete]);

  const skip = () => {
    if (page === 2) onComplete();
    else setPage((p) => p + 1);
  };
  const skipAll = () => onComplete();

  return (
    <div className={styles.root}>
      <div className={styles.topBar}>
        <button type="button" onClick={skipAll} className={styles.skipBtn}>
          Direkt zum Formular →
        </button>
      </div>
      <div className={styles.stage}>
        {page === 0 && (
          <div className={styles.pageOneWrap}>
            <div className={styles.eyebrow}>KFZ-Erfassung</div>
            <h1 className={styles.hero}>Mein Fahrzeug</h1>
            <div className={styles.heroSub}>
              {companyName || "Ihr Unternehmen"}
            </div>
          </div>
        )}
        {page === 1 && (
          <div className={styles.pageTwo}>
            Wir benötigen ein paar <span className={styles.bold}>Eckdaten</span>{" "}
            zu Ihrem Fahrzeug — von <span className={styles.bold}>Kennzeichen</span> über{" "}
            <span className={styles.bold}>Marke und Modell</span> bis zum{" "}
            <span className={styles.bold}>Wert</span>.
          </div>
        )}
        {page === 2 && (
          <div className={styles.tilesWrap}>
            <h2 className={styles.tilesHeading}>
              Vier kleine Schritte — und Ihr Fahrzeug ist erfasst
            </h2>
            <div className={styles.tileRow}>
              <div className={styles.tile}>
                <span className={styles.tileNumber}>1</span>
                <TileIcon.car />
                <span className={styles.tileLabel}>Fahrzeugart</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileNumber}>2</span>
                <TileIcon.plate />
                <span className={styles.tileLabel}>Kennzeichen</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileNumber}>3</span>
                <TileIcon.brand />
                <span className={styles.tileLabel}>Marke & Modell</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileNumber}>4</span>
                <TileIcon.euro />
                <span className={styles.tileLabel}>Wert & Anschaffung</span>
              </div>
            </div>
            <p className={styles.pageThreeNote}>
              Das Ausfüllen dauert in der Regel weniger als 5 Minuten.
            </p>
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.dots}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={mergeClasses(styles.dot, (i === page) && styles.dotActive)}
              />
            ))}
          </div>
          <button type="button" onClick={skip} className={styles.weiterBtn}>
            {page === 2 ? "Zum Formular" : "Weiter"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KfzWelcomeFlow;
